import React from 'react';
import clsx from 'clsx';
import { ViolationReturn, ViolationUpdate } from '../../clients/rust-api-client';
import { formatMoney } from '../../utils/billingHelpers';
import { fineStatus } from '../../billing/enumLabels';
import { SeverityGlyph, StatusIcon } from './StatusIcon';
import { GroupedList } from './GroupedList';
import { PropertyPicker } from './PropertyPicker';
import { RowCheckbox } from './RowCheckbox';
import { ListSelection } from './useListSelection';
import {
  SEVERITIES,
  STATUSES,
  ViolationGroup,
  formatDay,
  isOverdue,
  normalizeSeverity,
  normalizeStatus,
  shortRef,
  statusLabel,
} from './violationMeta';

/**
 * Linear's list view (#603 Part 2 §2): rows grouped by status under a
 * header with a count, one dense line per violation — severity, reference,
 * status, title — with the secondary facts as chips on the right. No cell
 * borders; a hover fill. Groups collapse.
 *
 * Presentational only: grouping, filtering and navigation belong to the
 * caller. Rows are buttons, so the list is fully keyboard-operable.
 */
/**
 * In-row editing: status and severity pickers that save on choose. With
 * `selection`, a checkbox per row. `onUpdate` hears every choice, even of
 * the value the row already has — the caller decides which rows a change
 * reaches, and other checked rows may not have it.
 */
export interface RowEditing {
  canEdit: boolean;
  /** `${violationId}:${field}` for each field saving now. */
  saving: ReadonlySet<string>;
  onUpdate: (violation: ViolationReturn, patch: ViolationUpdate, field: string) => void;
  selection?: ListSelection;
}

interface Props {
  groups: ViolationGroup[];
  onOpen: (violation: ViolationReturn) => void;
  /** Omit for a read-only list (Home's preview). */
  editing?: RowEditing;
  /** Show the fine chip (hidden where fines are not the reader's business). */
  showFine?: boolean;
  emptyMessage?: string;
}

const Chip: React.FC<{ className?: string; children: React.ReactNode; title?: string }> = ({
  className,
  children,
  title,
}) => (
  <span
    title={title}
    className={clsx(
      'inline-flex items-center gap-1 rounded-full border border-line px-2 py-0.5 text-[12px] leading-4 text-fg-muted',
      className
    )}
  >
    {children}
  </span>
);

// Keyed on FineStatus; colours follow billing/enumLabels' fineStatus.
const fineDot = (status: string) =>
  ({
    unpaid: 'bg-red-400',
    partially_paid: 'bg-amber-400',
    paid: 'bg-lime-400',
    reversed: 'bg-zinc-400',
  })[status] ?? 'bg-zinc-400';

/**
 * Icon-only chips for the pickers at the start of a row. `!` because
 * chipClass sets its own padding and Tailwind does not order conflicts.
 */
const rowPickerClass = '!px-1 !py-0.5';

/**
 * One violation as one dense line. The row is not itself a button: the
 * whole-row "open" button sits underneath as an overlay, and the severity
 * and status pickers sit above it — a button cannot contain buttons.
 * Everything else ignores the pointer, so a click anywhere but a picker
 * opens the violation.
 */
export const Row: React.FC<{
  violation: ViolationReturn;
  onOpen: (v: ViolationReturn) => void;
  showFine: boolean;
  editing?: RowEditing;
}> = ({ violation: v, onOpen, showFine, editing }) => {
  const overdue = isOverdue(v);
  const severity = normalizeSeverity(v.severity);
  const status = normalizeStatus(v.status);
  const ref = shortRef(v.id);
  const busy = (field: string) => !!editing?.saving.has(`${v.id}:${field}`);
  const readOnly = !editing?.canEdit;
  const selection = editing?.canEdit ? editing.selection : undefined;
  const selected = !!selection?.selected.has(v.id);

  return (
    <li
      className={clsx(
        'group relative flex items-center gap-3 px-4 py-2 text-[13px] has-[>button:focus-visible]:bg-fg/5',
        selected ? 'bg-accent/[0.08] hover:bg-accent/[0.11]' : 'hover:bg-fg/[0.03]'
      )}
    >
      <button
        type="button"
        onClick={() => onOpen(v)}
        className="absolute inset-0 focus:outline-none"
      >
        <span className="sr-only">
          {`Open ${ref}: ${v.description}. Severity ${severity}, ${statusLabel(v.status)}${overdue ? ', overdue' : ''}`}
        </span>
      </button>

      {selection && (
        <RowCheckbox
          checked={selected}
          onChange={(on) => {
            const next = new Set(selection.selected);
            if (on) next.add(v.id);
            else next.delete(v.id);
            selection.onChange(next);
          }}
          label={`Select ${ref}`}
        />
      )}

      {editing ? (
        <span className="relative z-10 -mx-1 inline-flex">
          <PropertyPicker
            options={SEVERITIES.map((s) => ({
              value: s,
              label: s,
              icon: <SeverityGlyph severity={s} />,
            }))}
            value={severity}
            onChange={(value) => editing.onUpdate(v, { severity: value }, 'severity')}
            placeholder="Change severity…"
            ariaLabel={`Severity of ${ref}: ${severity}${readOnly ? '' : '. Change severity'}`}
            readOnly={readOnly}
            busy={busy('severity')}
            className={rowPickerClass}
            trigger={<SeverityGlyph severity={v.severity} />}
          />
        </span>
      ) : (
        <SeverityGlyph severity={v.severity} className="pointer-events-none" />
      )}
      <span className="pointer-events-none hidden w-[72px] shrink-0 font-mono text-[12px] text-fg-muted sm:inline">
        {ref}
      </span>
      {editing ? (
        <span className="relative z-10 -mx-1 inline-flex">
          <PropertyPicker
            options={STATUSES.map((s) => ({
              value: s.value,
              label: s.label,
              icon: <StatusIcon status={s.value} />,
            }))}
            value={status}
            onChange={(value) => editing.onUpdate(v, { status: value }, 'status')}
            placeholder="Change status…"
            ariaLabel={`Status of ${ref}: ${statusLabel(v.status)}${readOnly ? '' : '. Change status'}`}
            readOnly={readOnly}
            busy={busy('status')}
            className={rowPickerClass}
            trigger={<StatusIcon status={v.status} />}
          />
        </span>
      ) : (
        <StatusIcon status={v.status} className="pointer-events-none" />
      )}
      <span className="pointer-events-none min-w-0 flex-1 truncate font-medium text-fg">
        {v.description}
      </span>
      <span className="pointer-events-none hidden items-center gap-1.5 md:flex">
        <Chip>{v.violation_type}</Chip>
        {showFine && v.fine && (
          <Chip title={`Fine: ${fineStatus.label(v.fine.status)}`}>
            <span className={clsx('h-1.5 w-1.5 rounded-full', fineDot(v.fine.status))} aria-hidden />
            {formatMoney(v.fine.amount)}
          </Chip>
        )}
      </span>
      {v.due_date && (
        <span
          className={clsx(
            'pointer-events-none hidden w-16 shrink-0 text-right text-[12px] sm:inline',
            overdue ? 'text-red-400' : 'text-fg-muted'
          )}
        >
          {formatDay(v.due_date)}
        </span>
      )}
    </li>
  );
};

export const GroupedViolationList: React.FC<Props> = ({
  groups,
  onOpen,
  showFine = true,
  emptyMessage = 'No violations match these filters.',
  editing,
}) => (
  <GroupedList
    groups={groups.map((g) => ({
      key: g.status,
      label: g.label,
      icon: <StatusIcon status={g.status} />,
      items: g.violations,
    }))}
    renderRow={(v) => (
      <Row
        key={v.id}
        violation={v}
        onOpen={onOpen}
        showFine={showFine}
        editing={editing}
      />
    )}
    idPrefix="violation-group"
    emptyMessage={emptyMessage}
    selection={
      editing?.canEdit && editing.selection
        ? { ...editing.selection, getKey: (v) => v.id }
        : undefined
    }
  />
);

